/* eslint-disable @typescript-eslint/no-empty-interface */

import { capitalCase, pascalCase } from 'change-case';
import { DocumentNode } from 'graphql';
import gql from 'graphql-tag';
import { DateRange, UTC } from 'rewire-common';
import {
  AreaInsertInput,
  AreaUpdateInput,
  AreaUpsertInput,
  BatchInsertInput,
  BatchUpdateInput,
  ConfirmUserInput,
  DeepPartial,
  defaultReturnSchema,
  EmployeeInsertInput,
  EmployeeUpdateInput,
  EmployeeUpsertInput,
  GeneratorInsertInput,
  GeneratorUpdateInput,
  GeneratorUpsertInput,
  GroupInsertInput,
  GroupUpdateInput,
  GroupUpsertInput,
  GroupViewUpsertInput,
  IArea,
  IAuthenticationOptions,
  IBatch,
  ICommand,
  IDocument,
  IEmployee,
  IEmployeeInfo,
  IEmployeeTimecardStatus,
  IGenerator,
  IGroup,
  IInsertResponseInput,
  IJob,
  ILocation,
  IMessage,
  IMoveJobResult,
  INameEntity,
  InvitationInput,
  IOccupation,
  IReason,
  IRole,
  IRuleSetTable,
  ITemplate,
  ITimecardTest,
  ITimeOffRequest,
  ITimeOffRequestStatus,
  ITimeOffType,
  IUdf,
  IUdt,
  IUdtInsertInput,
  IUdtUpdateInput,
  IUser,
  IView,
  JobInfoInput,
  JobInput,
  LocationInsertInput,
  LocationUpdateInput,
  LocationUpsertInput,
  MessageInsertInput,
  MessageUpdateInput,
  OccupationInsertInput,
  OccupationUpdateInput,
  OccupationUpsertInput,
  ReasonInsertInput,
  ReasonUpdateInput,
  ReasonUpsertInput,
  RoleInsertInput,
  RoleUpdateInput,
  SaveTimecardInput,
  TemplateInsertInput,
  TemplateUpdateInput,
  TemplateUpsertInput,
  TimecardInput,
  TimecardTestFilterInput,
  TimecardTestInsertInput,
  TimecardTestUpdateInput,
  TimeOffAccrualInsertInput,
  TimeOffAccrualUpdateInput,
  TimeOffRequestInsertInput,
  TimeOffRequestUpdateInput,
  TimeOffTypeInsertInput,
  TimeOffTypeUpdateInput,
  TimeOffTypeUpsertInput,
  UdfDocumentType,
  UdfInsertInput,
  UdfUpdateInput,
  UserInput,
  UserProfileInput,
  ValidationRuleInput,
  ViewInsertInput,
  ViewUpdateInput,
  ViewUpsertInput,
} from '../DataTypes';
import { notifications } from '../ui/Notifications';
import { guardedMutation, mutation } from './GraphQLClient';

const pingMutation: DocumentNode = gql`
  mutation($dummy: String) {
    ping(dummy: $dummy)
  }
`;

const sendCommandsMutation: DocumentNode = gql`
  mutation($commands: [CommandInput!]!) {
    sendCommands(commands: $commands)
  }
`;

const loginMutation: DocumentNode = gql`
  mutation($email: String!, $password: String!) {
    login(email: $email, password: $password)
  }
`;

const getAuthenticationFlowFromEmailMutation: DocumentNode = gql`
  mutation($email: String!) {
    getAuthenticationFlowFromEmail(email: $email)
  }
`;

const logoutMutation: DocumentNode = gql`
  mutation($sessionId: ID) {
    logout(sessionId: $sessionId)
  }
`;

const switchToLocationMutation: DocumentNode = gql`
  mutation($sessionId: ID!, $locationId: ID!, $password: String!) {
    switchToLocation(sessionId: $sessionId, locationId: $locationId, password: $password)
  }
`;

const signUpMutation: DocumentNode = gql`
  mutation($email: String) {
    signUp(email: $email)
  }
`;

const inviteMutation: DocumentNode = gql`
  mutation($invitation: InvitationInput!) {
    invite(invitation: $invitation)
  }
`;

const reactivateUserMutation: DocumentNode = gql`
  mutation($email: String) {
    reactivateUser(email: $email)
  }
`;

const deactivateUserMutation: DocumentNode = gql`
  mutation($email: String) {
    deactivateUser(email: $email)
  }
`;

const confirmUserMutation: DocumentNode = gql`
  mutation($token: String!, $email: String!, $newPassword: String!, $userProfile: ConfirmUserInput!) {
    confirmUser(token: $token, email: $email, newPassword: $newPassword, userProfile: $userProfile)
  }
`;

const confirmSingleSignOnUserMutation: DocumentNode = gql`
  mutation($token: String!, $email: String!, $userProfile: ConfirmUserInput!) {
    confirmUser(token: $token, email: $email, userProfile: $userProfile)
  }
`;

const forgotPasswordMutation: DocumentNode = gql`
  mutation($email: String) {
    forgotPassword(email: $email)
  }
`;

const confirmForgotPasswordMutation: DocumentNode = gql`
  mutation($token: String!, $email: String!, $newPassword: String!) {
    confirmForgotPassword(token: $token, email: $email, newPassword: $newPassword)
  }
`;

// *** Return Schema ***
// data: [Location!]!
// id
// code
// name
// tags
// fields
// organization
// udfs
// areas
// users
// tz
// timeFormat
// postedDate
// workflow
// country
// state
// timeOffRequestWorkflow
const insertLocationsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($locations: [LocationInsertInput!]!) {
    insertLocations(locations: $locations) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateLocationsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($locations: [LocationUpdateInput!]!) {
    updateLocations(locations: $locations) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertLocationsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($locations: [LocationUpsertInput!]!) {
    upsertLocations(locations: $locations) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteLocationsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteLocations(ids: $ids)
  }
`;

const generateApiKeyMutation = gql`
  mutation($name: String!, $right: SecurityRight!) {
    generateApiKey(name: $name, right: $right)
  }
`;

const saveAuthenticationOptionsMutation = gql`
  mutation($apple: Boolean!, $saml: Boolean!, $usernamePassword: Boolean!, $mfa: Boolean!, $provider: SAMLIdentityProviderInput) {
    saveAuthenticationOptions(apple: $apple, saml: $saml, usernamePassword: $usernamePassword, mfa: $mfa, provider: $provider)
  }
`;

const removeApiKeyMutation = gql`
  mutation($name: String!) {
    removeApiKey(name: $name)
  }
`;
// *** Return Scehma ***
// id
// code
// name
// location
// organization
// tags
// fields
// groups
const insertAreasMutation = (returnSchema: string): DocumentNode => gql`
  mutation($areas: [AreaInsertInput!]!) {
    insertAreas(areas: $areas) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateAreasMutation = (returnSchema: string): DocumentNode => gql`
  mutation($areas: [AreaUpdateInput!]!) {
    updateAreas(areas: $areas) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertAreasMutation = (returnSchema: string): DocumentNode => gql`
  mutation($areas: [AreaUpsertInput!]!) {
    upsertAreas(areas: $areas) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteAreasMutation: DocumentNode = gql`
  mutation($ids: [ID!]!, $endDate: Date) {
    deleteAreas(ids: $ids, endDate: $endDate)
  }
`;

const duplicateAreaMutation: DocumentNode = gql`
  mutation($areaId: String!, $name: String!, $effective: DateRange!) {
    duplicateArea(areaId: $areaId, name: $name, effective: $effective)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// position
// area
const insertGroupsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($groups: [GroupInsertInput!]!) {
    insertGroups(groups: $groups) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateGroupsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($groups: [GroupUpdateInput!]!) {
    updateGroups(groups: $groups) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertGroupsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($groups: [GroupUpsertInput!]!) {
    upsertGroups(groups: $groups) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertGroupViewMutation = gql`
  mutation($group: GroupViewUpsertInput!) {
    upsertGroupView(group: $group)
  }
`;

const deleteGroupsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!, $endDate: Date) {
    deleteGroups(ids: $ids, endDate: $endDate)
  }
`;

const deleteResponsesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteResponses(ids: $ids)
  }
`;

const duplicateGroupMutation: DocumentNode = gql`
  mutation($areaId: String!, $groupId: String!, $name: String, $effective: DateRange) {
    duplicateGroup(areaId: $areaId, groupId: $groupId, name: $name, effective: $effective)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// email
// phone
// fields
// timecards
const insertEmployeesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($employees: [EmployeeInsertInput!]!) {
    insertEmployees(employees: $employees) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateEmployeesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($employees: [EmployeeUpdateInput!]!) {
    updateEmployees(employees: $employees) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertEmployeesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($employees: [EmployeeUpsertInput!]!) {
    upsertEmployees(employees: $employees) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteEmployeesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!, $endDate: Date) {
    deleteEmployees(ids: $ids, endDate: $endDate)
  }
`;

const insertRolesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($roles: [RoleInsertInput!]!) {
    insertRoles(roles: $roles) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateRolesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($roles: [RoleUpdateInput!]!) {
    updateRoles(roles: $roles) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteRolesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteRoles(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// text
// level
// type
// effective
// jobId
// createdBy
// createdDate
// employee {}
// batch {}
// group {}
const insertMessagesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($messages: [MessageInsertInput!]!) {
    insertMessages(messages: $messages) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateMessagesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($messages: [MessageUpdateInput!]!) {
    updateMessages(messages: $messages) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteMessagesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteMessages(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// cycle
// locked
// displayStartDate
const insertGeneratorsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($generators: [GeneratorInsertInput!]!) {
    insertGenerators(generators: $generators) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateGeneratorsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($generators: [GeneratorUpdateInput!]!) {
    updateGenerators(generators: $generators) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertGeneratorsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($generators: [GeneratorUpsertInput!]!) {
    upsertGenerators(generators: $generators) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteGeneratorsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteGenerators(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// group
// generator
// occupation
// employee
// cycleOffset
// position
// effective
// fields
const insertJobsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($jobInput: [JobInput!]!, $before: ID, $removeOverlappingAssignments: Boolean) {
    insertJobs(jobInput: $jobInput, before: $before, removeOverlappingAssignments: $removeOverlappingAssignments) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertJobsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($jobInput: [JobInput!]!, $removeOverlappingAssignments: Boolean) {
    upsertJobs(jobInput: $jobInput, removeOverlappingAssignments: $removeOverlappingAssignments) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteJobsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteJobs(ids: $ids)
  }
`;

/******* Don't yet exist *******/
const archiveJobsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    archiveJobs(ids: $ids)
  }
`;

const unarchiveJobsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    unarchiveJobs(ids: $ids)
  }
`;
/*******************************/

// *** Return Schema ***
// id
// group
// generator
// occupation
// employee
// cycleOffset
// position
// effective
// fields
const updateJobEffectiveRangeMutation = (returnSchema: string): DocumentNode => gql`
  mutation($jobId: ID!, $newEffective: DateRange!, $removeOverlappingAssignments: Boolean) {
    updateJobEffectiveRange(jobId: $jobId, newEffective: $newEffective, removeOverlappingAssignments: $removeOverlappingAssignments) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const moveJobMutation: DocumentNode = gql`
  mutation($jobId: ID!, $before: ID) {
    moveJob(jobId: $jobId, before: $before) {
      id
      position
    }
  }
`;

// *** Return Schema ***
// id
// group
// generator
// occupation
// employee
// cycleOffset
// position
// effective
// fields
const endEmployeeAssignmentMutation = (returnSchema: string): DocumentNode => gql`
  mutation($jobId: ID!, $assignmentEndDate: Date!, $employeeId: ID) {
    endEmployeeAssignment(jobId: $jobId, assignmentEndDate: $assignmentEndDate, employeeId: $employeeId) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
const insertOccupationsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($occupations: [EntityInsertInput!]!) {
    insertOccupations(occupations: $occupations) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateOccupationsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($occupations: [EntityUpdateInput!]!) {
    updateOccupations(occupations: $occupations) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertOccupationsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($occupations: [EntityUpsertInput!]!) {
    upsertOccupations(occupations: $occupations) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteOccupationsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteOccupations(ids: $ids)
  }
`;

const setTimecardWorkflowMutation: DocumentNode = gql`
  mutation($workflow: WorkflowInput!) {
    setWorkflow(workflow: $workflow)
  }
`;

const setManualTimecardMutation: DocumentNode = gql`
  mutation($employeeId: ID!, $earnedDate: Date!, $manual: Boolean!) {
    setManualTimecard(employeeId: $employeeId, earnedDate: $earnedDate, manual: $manual)
  }
`;

const transitionTimecardsMutation: DocumentNode = gql`
  mutation($employeeIds: [String!]!, $effective: DateRange!, $event: String!, $noResults: Boolean = false) {
    transitionTimecards(employeeIds: $employeeIds, effective: $effective, event: $event, noResults: $noResults) {
      employeeId
      earnedDate
      status
      isPayable
      lastEditedDate
      lastEditedBy
      locked
      manual
      batch {
        id
        name
        status
      }
      allowableTransitions {
        event
        enabled
      }
    }
  }
`;

// *** Return Schema ***
// id
// locationId
// name
// displayName
const insertUserDefinedTypesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($udts: [UserDefinedTypeInsertInput!]!) {
    insertUserDefinedTypes(udts: $udts) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateUserDefinedTypesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($udts: [UserDefinedTypeUpdateInput!]!) {
    updateUserDefinedTypes(udts: $udts) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteUserDefinedTypesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteUserDefinedTypes(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
const insertDocumentsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($documents: [DocumentInsertInput!]!) {
    insertDocuments(documents: $documents) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateDocumentsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($documents: [DocumentUpdateInput!]!) {
    updateDocuments(documents: $documents) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const deleteDocumentsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteDocuments(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// category
const insertReasonsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($reasons: [ReasonInsertInput!]!) {
    insertReasons(reasons: $reasons) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateReasonsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($reasons: [ReasonUpdateInput!]!) {
    updateReasons(reasons: $reasons) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertReasonsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($reasons: [ReasonUpsertInput!]!) {
    upsertReasons(reasons: $reasons) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteReasonsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteReasons(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// category
const insertTimeOffTypesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($timeOffTypes: [TimeOffTypeInsertInput!]!) {
    insertTimeOffTypes(timeOffTypes: $timeOffTypes) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateTimeOffTypesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($timeOffTypes: [TimeOffTypeUpdateInput!]!) {
    updateTimeOffTypes(timeOffTypes: $timeOffTypes) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertTimeOffTypesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($timeOffTypes: [TimeOffTypeUpsertInput!]!) {
    upsertTimeOffTypes(timeOffTypes: $timeOffTypes) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteTimeOffTypesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteTimeOffTypes(ids: $ids)
  }
`;

const setTimeOffRequestWorkflowMutation: DocumentNode = gql`
  mutation($timeOffRequestWorkflow: WorkflowInput!) {
    setTimeOffRequestWorkflow(workflow: $timeOffRequestWorkflow)
  }
`;

// *** Return Schema ***
// id
// code
// name
// version
// tags
// fields
// template
// locked
const insertTemplatesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($templates: [TemplateInsertInput!]!) {
    insertTemplates(templates: $templates) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateTemplatesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($templates: [TemplateUpdateInput!]!) {
    updateTemplates(templates: $templates) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertTemplatesMutation = (returnSchema: string): DocumentNode => gql`
  mutation($templates: [TemplateUpsertInput!]!) {
    upsertTemplates(templates: $templates) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteTemplatesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteTemplates(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// documentType
// type
// name
// isSystem
// isList
// displayName
// allowableValues
// location
const insertUdfsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($udfInput: [UdfInsertInput!]!) {
    insertUdfs(udfInput: $udfInput) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateUdfsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($udfInput: [UdfUpdateInput!]!) {
    updateUdfs(udfInput: $udfInput) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteUdfsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteUdfs(ids: $ids)
  }
`;

// *** Return Schema ***
// id
// code
// name
// email
// avatar
// phone
// employee
// tags
// fields
// organization
// location
// userRights
const updateUsersMutation = (returnSchema: string): DocumentNode => gql`
mutation($users: [UserInput!]!) {
  updateUsers(users: $users) {
    ${returnSchema || defaultReturnSchema}
  }
}
`;

const updateUserProfileMutation: DocumentNode = gql`
  mutation($user: UserProfileInput!) {
    updateUserProfile(user: $user)
  }
`;

const deleteUserMutation: DocumentNode = gql`
  mutation($email: String!) {
    deleteUser(email: $email)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// type
// options
const insertViewsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($views: [ViewInsertInput!]!) {
    insertViews(views: $views) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const updateViewsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($views: [ViewUpdateInput!]!) {
    updateViews(views: $views) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;
const upsertViewsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($views: [ViewUpsertInput!]!) {
    upsertViews(views: $views) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteViewsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteViews(ids: $ids)
  }
`;

const insertBatchMutation: DocumentNode = gql`
  mutation($batch: InsertBatchInput!) {
    insertBatch(batch: $batch) {
      id
    }
  }
`;

const insertAdjustmentBatchMutation: DocumentNode = gql`
  mutation($batch: InsertBatchInput!) {
    insertAdjustmentBatch(batch: $batch) {
      id
    }
  }
`;

const undoReversalsMutation: DocumentNode = gql`
  mutation($employeeInfo: [EmployeeInfoInput!]!) {
    undoReversals(employeeInfo: $employeeInfo)
  }
`;
// const updateBatchesMutation: DocumentNode = gql`
//   mutation($batches: [EntityUpdateInput!]!) {
//     updateBatches(batches: $batches) {
//       id
//     }
//   }
// `;
// const upsertBatchesMutation: DocumentNode = gql`
//   mutation($batches: [EntityUpsertInput!]!) {
//     upsertBatches(batches: $batches) {
//       id
//       name
//     }
//   }
// `;

const exportBatchMutation: DocumentNode = gql`
  mutation($id: ID!) {
    exportBatch(id: $id)
  }
`;

// const undoBatchMutation: DocumentNode = gql`
//   mutation($id: ID!) {
//     undoBatch(id: $id)
//   }
// `;

// const closeBatchMutation: DocumentNode = gql`
//   mutation($id: ID!) {
//     closeBatch(id: $id)
//   }
// `;

const deleteBatchesMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteBatches(ids: $ids)
  }
`;

const regenerateExportFile: DocumentNode = gql`
  mutation($batchId: ID!) {
    regenerateExportFile(batchId: $batchId)
  }
`;

const uploadFileMutation: DocumentNode = gql`
  mutation($file: Upload!) {
    uploadFile(file: $file) {
      id
    }
  }
`;

const uploadFilesMutation: DocumentNode = gql`
  mutation($files: [Upload!]!) {
    uploadFiles(files: $files) {
      id
    }
  }
`;

const saveEmployeeTimecardsMutation: DocumentNode = gql`
  mutation($timecards: [SaveTimecardInput!]!) {
    saveEmployeeTimecards(timecards: $timecards)
  }
`;

const saveTestEmployeeTimecardsMutation = gql`
  mutation($timecards: [SaveTimecardInput!]!) {
    saveTestEmployeeTimecards(timecards: $timecards)
  }
`;

const runTimecardRulesMutation: DocumentNode = gql`
  mutation($employeeId: ID!, $effective: DateRange!) {
    runTimecardRules(employeeId: $employeeId, effective: $effective)
  }
`;

const runTestTimecardRulesMutation: DocumentNode = gql`
  mutation($employeeId: ID!, $effective: DateRange!) {
    runTestTimecardRules(employeeId: $employeeId, effective: $effective)
  }
`;

const saveRuleTableMutation: DocumentNode = gql`
  mutation($table: RuleTableInput!) {
    saveRuleTable(table: $table)
  }
`;

// *** Return Schema ***
// id
// schedule (shift[])
const saveJobTimecardsMutation: DocumentNode = gql`
  mutation($timecards: [TimecardInput!]!) {
    saveJobTimecards(timecards: $timecards)
  }
`;

const deleteJobTimecardsMutation: DocumentNode = gql`
  mutation($jobInfo: [JobInfoInput]) {
    deleteJobTimecards(jobInfo: $jobInfo)
  }
`;

const runTestsMutation: DocumentNode = gql`
  mutation($filter: TimecardTestFilterInput) {
    runTests(filter: $filter)
  }
`;

// *** Return Schema ***
// id
// code
// name
// tags
// fields
// employee
// effective
// area
// group
// occupation
// generator
// total
// difference
// status
// lastRan
// lastEditedBy
// testDetails (ITimecardTestDetail[])
const insertTimecardTestsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($timecardTests: [TimecardTestInsertInput!]!) {
    insertTimecardTests(timecardTests: $timecardTests) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const insertResponsesMutation = (returnSchema?: string): DocumentNode => gql`
  mutation($responses: [ResponseInsertInput!]!) {
    insertResponses(responses: $responses) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const updateTimecardTestsMutation = (returnSchema: string): DocumentNode => gql`
  mutation($timecardTests: [TimecardTestUpdateInput!]!) {
    updateTimecardTests(timecardTests: $timecardTests) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteTimecardTestsMutation: DocumentNode = gql`
  mutation($ids: [ID!]!) {
    deleteTimecardTests(ids: $ids)
  }
`;

const insertTimeOffAccrualMutation: DocumentNode = gql`
  mutation insertTimeOffAccrual($timeOffAccruals: [TimeOffAccrualInsertInput!]!) {
    insertTimeOffAccruals(timeOffAccruals: $timeOffAccruals) {
      id
      employee {
        id
        name
      }
      effective
      units
      amount
      timeOffType {
        id
        name
        trackingUnits
        trackingAmounts
      }
    }
  }
`;

const insertTimeOffRequestsMutation = (returnSchema: string): DocumentNode => gql`
  mutation insertTimeOffRequests($timeOffRequest: [TimeOffRequestInsertInput!]!) {
    insertTimeOffRequests(timeOffRequest: $timeOffRequest) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const updateTimeOffRequestsMutation = (returnSchema: string): DocumentNode => gql`
  mutation updateTimeOffRequests($timeOffRequest: [TimeOffRequestUpdateInput!]!) {
    updateTimeOffRequests(timeOffRequest: $timeOffRequest) {
      ${returnSchema || defaultReturnSchema}
    }
  }
`;

const deleteTimeOffRequestsMutation: DocumentNode = gql`
  mutation deleteTimeOffRequests($ids: [ID!]!) {
    deleteTimeOffRequests(ids: $ids)
  }
`;

const transitionTimeOffRequestMutation: DocumentNode = gql`
  mutation($timeOffRequestId: ID!, $event: String!) {
    transitionTimeOffRequest(timeOffRequestId: $timeOffRequestId, event: $event) {
      id
      status
      allowableTransitions {
        event
        enabled
      }
    }
  }
`;

const updateTimeOffAccrualMutation: DocumentNode = gql`
  mutation updateTimeOffAccrual($timeOffAccruals: [TimeOffAccrualUpdateInput!]!) {
    updateTimeOffAccruals(timeOffAccruals: $timeOffAccruals) {
      id
      employee {
        id
        name
      }
      effective
      units
      amount
      timeOffType {
        id
        name
        trackingUnits
        trackingAmounts
      }
    }
  }
`;

const deleteTimeOffAccrualsMutation: DocumentNode = gql`
  mutation deleteTimeOffAccrual($ids: [ID!]!) {
    deleteTimeOffAccruals(ids: $ids)
  }
`;

const saveValidationRulesMutation: DocumentNode = gql`
  mutation($rules: [ValidationRuleInput!]!) {
    saveValidationRules(rules: $rules)
  }
`;

const postScheduleMutation: DocumentNode = gql`
  mutation($postedDate: Date!) {
    postSchedule(postedDate: $postedDate)
  }
`;

const runAutoScheduleMutation: DocumentNode = gql`
  mutation($jobsToFill: [AutoScheduleJobInput!]!) {
    runAutoSchedule(jobsToFill: $jobsToFill)
  }
`;

export default class DataMutations {
  static async ping(dummy?: string): Promise<string> {
    const result = await mutation(pingMutation, { dummy: dummy });
    return result?.data?.ping;
  }

  static async login(email: string, password: string): Promise<string | undefined> {
    const result = await mutation(loginMutation, { email: email, password: password });
    return result?.data?.login;
  }

  static async sendCommands(commands: ICommand[]): Promise<boolean> {
    const result = await mutation(sendCommandsMutation, { commands });
    return result?.data?.sendCommands;
  }

  static async getAuthenticationFlowFromEmail(email: string): Promise<string | undefined> {
    const result = await mutation(getAuthenticationFlowFromEmailMutation, { email: email });
    return result?.data?.getAuthenticationFlowFromEmail;
  }

  static async logout(sessionId: string): Promise<string | undefined> {
    const result = await guardedMutation(logoutMutation, { sessionId: sessionId });
    return result?.data?.logout;
  }

  static async switchToLocation(sessionId: string, locationId: string, password: string): Promise<string> {
    const result = await guardedMutation(switchToLocationMutation, { locationId, sessionId, password });
    return result?.data?.switchToLocation;
  }

  static async signUp(email?: string): Promise<boolean | undefined> {
    const result = await mutation(signUpMutation, { email: email });
    return result?.data?.signUp;
  }

  static async invite(invitation: InvitationInput): Promise<boolean | undefined> {
    const result = await guardedMutation(inviteMutation, { invitation: invitation });
    return result?.data?.invite;
  }

  static async reactivateUser(email?: string): Promise<boolean | undefined> {
    const result = await guardedMutation(reactivateUserMutation, { email: email });
    return result?.data?.reactivateUser;
  }

  static async deactivateUser(email?: string): Promise<boolean | undefined> {
    const result = await guardedMutation(deactivateUserMutation, { email: email });
    return result?.data?.deactivateUser;
  }

  static async confirmUser(
    token: string,
    email: string,
    newPassword: string,
    userProfile: ConfirmUserInput,
  ): Promise<string | undefined> {
    const result = await mutation(confirmUserMutation, {
      token: token,
      email: email,
      newPassword: newPassword,
      userProfile: userProfile,
    });
    return result?.data?.confirmUser;
  }

  static async confirmSingleSignOnUser(token: string, email: string, userProfile: ConfirmUserInput): Promise<string | undefined> {
    const result = await mutation(confirmSingleSignOnUserMutation, { token: token, email: email, userProfile: userProfile });
    return result?.data?.confirmSingleSignOnUser;
  }

  static async updateUsers(returnSchema: string, users: UserInput[]): Promise<DeepPartial<IUser>[] | undefined> {
    const result = await guardedMutation(updateUsersMutation(returnSchema), { users: users });
    return result?.data?.updateUsers;
  }

  static async updateUserProfile(user: UserProfileInput): Promise<boolean | undefined> {
    const result = await guardedMutation(updateUserProfileMutation, { user: user });
    return result?.data?.updateUserProfile;
  }

  static async deleteUser(email: string): Promise<boolean | undefined> {
    const result = await guardedMutation(deleteUserMutation, { email: email });
    return result?.data?.deleteUser;
  }

  static async forgotPassword(email?: string): Promise<string | undefined> {
    const result = await mutation(forgotPasswordMutation, { email: email });
    return result?.data?.forgotPassword;
  }

  static async confirmForgotPassword(token?: string, email?: string, newPassword?: string): Promise<string | undefined> {
    const result = await mutation(confirmForgotPasswordMutation, { token: token, email: email, newPassword: newPassword });
    return result?.data?.confirmForgotPassword;
  }

  static async insertLocations(
    returnSchema: string,
    locations: LocationInsertInput[],
  ): Promise<DeepPartial<ILocation>[] | undefined> {
    const result = await guardedMutation(insertLocationsMutation(returnSchema), { locations: locations });
    return result?.data?.insertLocations;
  }

  static async saveAuthenticationOptions(options: IAuthenticationOptions): Promise<boolean> {
    const result = await guardedMutation(saveAuthenticationOptionsMutation, options as unknown as Record<string, unknown>);
    return result?.data?.saveAuthenticationOptions;
  }

  static async generateApiKey(name: string, right: 'write' | 'read'): Promise<string | undefined> {
    const result = await guardedMutation(generateApiKeyMutation, { name, right });
    return result?.data?.generateApiKey;
  }

  static async removeApiKey(name: string): Promise<string | undefined> {
    return (await guardedMutation(removeApiKeyMutation, { name }))?.data?.removeApiKey;
  }

  static async updateLocations(
    returnSchema: string,
    locations: LocationUpdateInput[],
  ): Promise<DeepPartial<ILocation>[] | undefined> {
    const tasks = [];
    for (const location of locations) {
      if (location?.workflow?.id) {
        const fields = location.workflow.fields;
        tasks.push(guardedMutation(setTimecardWorkflowMutation, { workflow: { id: location.workflow.id, fields } }));
      }
      if (location.timeOffRequestWorkflow?.id) {
        tasks.push(
          guardedMutation(setTimeOffRequestWorkflowMutation, {
            timeOffRequestWorkflow: { id: location.timeOffRequestWorkflow.id },
          }),
        );
      }
      delete location.workflow;
      delete location.timeOffRequestWorkflow;
    }
    tasks.unshift(guardedMutation(updateLocationsMutation(returnSchema), { locations: locations }));
    const [r1] = await Promise.all(tasks);
    return r1?.data?.updateLocations;
  }

  static async upsertLocations(
    returnSchema: string,
    locations: LocationUpsertInput[],
  ): Promise<DeepPartial<ILocation>[] | undefined> {
    const result = await guardedMutation(upsertLocationsMutation(returnSchema), { locations: locations });
    return result?.data?.upsertLocations;
  }

  static async deleteLocations(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteLocationsMutation, { ids: ids });
    return result?.data?.deleteLocations;
  }

  static async insertAreas(returnSchema: string, areas: AreaInsertInput[]): Promise<DeepPartial<IArea>[] | undefined> {
    const result = await guardedMutation(insertAreasMutation(returnSchema), { areas: areas });
    return result?.data?.insertAreas;
  }

  static async updateAreas(returnSchema: string, areas: AreaUpdateInput[]): Promise<DeepPartial<IArea>[] | undefined> {
    const result = await guardedMutation(updateAreasMutation(returnSchema), { areas: areas });
    return result?.data?.updateAreas;
  }

  static async upsertAreas(returnSchema: string, areas: AreaUpsertInput[]): Promise<DeepPartial<IArea>[] | undefined> {
    const result = await guardedMutation(upsertAreasMutation(returnSchema), { areas: areas });
    return result?.data?.upsertAreas;
  }

  static async deleteAreas(ids: string[], endDate?: UTC): Promise<string | undefined> {
    const result = await guardedMutation(deleteAreasMutation, { ids: ids, endDate: endDate });
    return result?.data?.deleteAreas;
  }

  static async duplicateArea(areaId: string, name: string, effective: DateRange): Promise<boolean | undefined> {
    const result = await guardedMutation(duplicateAreaMutation, { areaId: areaId, name: name, effective: effective });
    return result?.data?.duplicateArea;
  }

  static async insertGroups(returnSchema: string, groups: GroupInsertInput[]): Promise<DeepPartial<IGroup>[] | undefined> {
    const result = await guardedMutation(insertGroupsMutation(returnSchema), { groups: groups });
    return result?.data?.insertGroups;
  }

  static async updateGroups(returnSchema: string, groups: GroupUpdateInput[]): Promise<DeepPartial<IGroup>[] | undefined> {
    const result = await guardedMutation(updateGroupsMutation(returnSchema), { groups: groups });
    // TODO: updateGroups is currently returning null from the server, converted to undefined here, even in the case of success
    // return groups.map(g => { return { id: g.id }; });
    return result?.data?.updateGroups;
  }

  static async upsertGroups(returnSchema: string, groups: GroupUpsertInput[]): Promise<DeepPartial<IGroup>[] | undefined> {
    const result = await guardedMutation(upsertGroupsMutation(returnSchema), { groups: groups });
    return result?.data?.upsertGroups;
  }

  static async upsertGroupView(group: GroupViewUpsertInput): Promise<boolean> {
    const result = await guardedMutation(upsertGroupViewMutation, { group: group });
    return result?.data?.upsertGroupView;
  }

  static async deleteGroups(ids: string[], endDate?: UTC): Promise<string | undefined> {
    const result = await guardedMutation(deleteGroupsMutation, { ids: ids, endDate: endDate });
    return result?.data?.deleteGroups;
  }

  static async duplicateGroup(areaId: string, groupId: string, name: string, effective: DateRange): Promise<boolean | undefined> {
    const result = await guardedMutation(duplicateGroupMutation, {
      areaId: areaId,
      groupId: groupId,
      name: name,
      effective: effective,
    });
    return result?.data?.duplicateGroup;
  }

  static async deleteResponses(ids: string[]): Promise<boolean | undefined> {
    const result = await guardedMutation(deleteResponsesMutation, { ids: ids });
    return result?.data?.deleteResponses;
  }

  static async insertEmployees(
    returnSchema: string,
    employees: EmployeeInsertInput[],
  ): Promise<DeepPartial<IEmployee>[] | undefined> {
    const result = await guardedMutation(insertEmployeesMutation(returnSchema), { employees: employees });
    return result?.data?.insertEmployees;
  }

  static async updateEmployees(
    returnSchema: string,
    employees: EmployeeUpdateInput[],
  ): Promise<DeepPartial<IEmployee>[] | undefined> {
    const result = await guardedMutation(updateEmployeesMutation(returnSchema), { employees: employees });
    return result?.data?.updateEmployees;
  }

  static async upsertEmployees(
    returnSchema: string,
    employees: EmployeeUpsertInput[],
  ): Promise<DeepPartial<IEmployee>[] | undefined> {
    const result = await guardedMutation(upsertEmployeesMutation(returnSchema), { employees: employees });
    return result?.data?.upsertEmployees;
  }

  static async deleteEmployees(ids: string[], endDate?: UTC): Promise<boolean | undefined> {
    const result = await guardedMutation(deleteEmployeesMutation, { ids: ids, endDate: endDate });
    return result?.data?.deleteEmployees;
  }

  static async insertRoles(returnSchema: string, roles: RoleInsertInput[]): Promise<DeepPartial<IRole>[] | undefined> {
    const result = await guardedMutation(insertRolesMutation(returnSchema), { roles: roles });
    return result?.data?.insertRoles;
  }

  static async updateRoles(returnSchema: string, roles: RoleUpdateInput[]): Promise<DeepPartial<IRole>[] | undefined> {
    const result = await guardedMutation(updateRolesMutation(returnSchema), { roles: roles });
    return result?.data?.updateRoles;
  }

  static async deleteRoles(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteRolesMutation, { ids: ids });
    return result?.data?.deleteRoles;
  }

  static async duplicateRole(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteRolesMutation, { ids: ids });
    return result?.data?.deleteRoles;
  }

  static async insertMessages(returnSchema: string, messages: MessageInsertInput[]): Promise<DeepPartial<IMessage>[] | undefined> {
    const result = await guardedMutation(insertMessagesMutation(returnSchema), { messages });
    return result?.data?.insertMessages;
  }

  static async updateMessages(returnSchema: string, messages: MessageUpdateInput[]): Promise<DeepPartial<IMessage>[] | undefined> {
    const result = await guardedMutation(updateMessagesMutation(returnSchema), { messages });
    return result?.data?.updateGenerators;
  }

  static async deleteMessages(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteMessagesMutation, { ids: ids });
    return result?.data?.deleteMessages;
  }

  static async insertGenerators(
    returnSchema: string,
    generators: GeneratorInsertInput[],
  ): Promise<DeepPartial<IGenerator>[] | undefined> {
    const result = await guardedMutation(insertGeneratorsMutation(returnSchema), { generators: generators });
    return result?.data?.insertGenerators;
  }

  static async updateGenerators(
    returnSchema: string,
    generators: GeneratorUpdateInput[],
  ): Promise<DeepPartial<IGenerator>[] | undefined> {
    const result = await guardedMutation(updateGeneratorsMutation(returnSchema), { generators: generators });
    return result?.data?.updateGenerators;
  }

  static async upsertGenerators(
    returnSchema: string,
    generators: GeneratorUpsertInput[],
  ): Promise<DeepPartial<IGenerator>[] | undefined> {
    const result = await guardedMutation(upsertGeneratorsMutation(returnSchema), { generators: generators });
    return result?.data?.upsertGenerators;
  }

  static async deleteGenerators(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteGeneratorsMutation, { ids: ids });
    return result?.data?.deleteGenerators;
  }

  static async insertJobs(
    returnSchema: string,
    jobInput: JobInput[],
    removeOverlappingAssignments: boolean,
    before?: string,
  ): Promise<DeepPartial<IJob>[] | undefined> {
    const result = await guardedMutation(insertJobsMutation(returnSchema), {
      jobInput: jobInput,
      before: before,
      removeOverlappingAssignments: removeOverlappingAssignments,
    });
    return result?.data?.insertJobs;
  }

  static async upsertJobs(
    returnSchema: string,
    jobInput: JobInput[],
    removeOverlappingAssignments: boolean,
  ): Promise<DeepPartial<IJob>[] | undefined> {
    const result = await guardedMutation(upsertJobsMutation(returnSchema), {
      jobInput: jobInput,
      removeOverlappingAssignments: removeOverlappingAssignments,
    });
    return result?.data?.upsertJobs;
  }

  static async deleteJobs(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteJobsMutation, { ids: ids });
    return result?.data?.deleteJobs;
  }

  static async archiveJobs(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(archiveJobsMutation, { ids: ids });
    return result?.data?.archiveJobs;
  }

  static async unarchiveJobs(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(unarchiveJobsMutation, { ids: ids });
    return result?.data?.unarchiveJobs;
  }

  static async updateJobEffectiveRange(
    returnSchema: string,
    jobId: string,
    newEffective: DateRange,
    removeOverlappingAssignments: boolean,
  ): Promise<IJob | undefined> {
    const result = await guardedMutation(updateJobEffectiveRangeMutation(returnSchema), {
      jobId: jobId,
      newEffective: newEffective,
      removeOverlappingAssignments: removeOverlappingAssignments,
    });
    return result?.data?.updateJobEffectiveRange;
  }

  static async moveJob(jobId: string, beforeJobId?: string): Promise<IMoveJobResult | undefined> {
    const result = await guardedMutation(moveJobMutation, { jobId: jobId, before: beforeJobId });
    return result?.data?.moveJob;
  }

  static async endEmployeeAssignment(
    returnSchema: string,
    jobId: string,
    assignmentEndDate: UTC,
    employeeId?: string,
  ): Promise<IJob[] | undefined> {
    const result = await guardedMutation(endEmployeeAssignmentMutation(returnSchema), {
      jobId: jobId,
      assignmentEndDate: assignmentEndDate,
      employeeId: employeeId,
    });
    return result?.data?.endEmployeeAssignment;
  }

  static async insertOccupations(
    returnSchema: string,
    occupations: OccupationInsertInput[],
  ): Promise<DeepPartial<IOccupation>[] | undefined> {
    const result = await guardedMutation(insertOccupationsMutation(returnSchema), { occupations: occupations });
    return result?.data?.insertOccupations;
  }

  static async updateOccupations(
    returnSchema: string,
    occupations: OccupationUpdateInput[],
  ): Promise<DeepPartial<IOccupation>[] | undefined> {
    const result = await guardedMutation(updateOccupationsMutation(returnSchema), { occupations: occupations });
    return result?.data?.updateOccupations;
  }

  static async upsertOccupations(
    returnSchema: string,
    occupations: OccupationUpsertInput[],
  ): Promise<DeepPartial<IOccupation>[] | undefined> {
    const result = await guardedMutation(upsertOccupationsMutation(returnSchema), { occupations: occupations });
    return result?.data?.upsertOccupations;
  }

  static async deleteOccupations(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteOccupationsMutation, { ids });
    return result?.data?.deleteOccupations;
  }

  static async setWorkflow(workflowId: string): Promise<boolean> {
    const result = await guardedMutation(setTimecardWorkflowMutation, { workflowId });
    return result?.data?.setWorkflow;
  }

  static async setManualTimecard(employeeId: string, earnedDate: UTC, manual: boolean): Promise<boolean> {
    const result = await guardedMutation(setManualTimecardMutation, { employeeId, earnedDate, manual });
    return result?.data?.setManualTimecard;
  }

  static async setTimeOffRequestWorkflow(workflowId: string): Promise<boolean> {
    const result = await guardedMutation(setTimeOffRequestWorkflowMutation, { workflowId });
    return result?.data?.setTimeOffRequestWorkflow;
  }

  static async transitionTimecards(
    employeeIds: string[],
    effective: DateRange,
    event: string,
    noResults: boolean = false,
  ): Promise<IEmployeeTimecardStatus[] | undefined> {
    const result = await guardedMutation(transitionTimecardsMutation, { employeeIds, effective, event, noResults });
    return result?.data?.transitionTimecards;
  }

  static async insertDocuments(
    type: string,
    returnSchema: string,
    documents: IDocument[],
  ): Promise<DeepPartial<INameEntity>[] | undefined> {
    for (const d of documents) d.type = type;
    const result = await guardedMutation(insertDocumentsMutation(returnSchema), { documents });
    return result?.data?.insertDocuments;
  }

  static async updateDocuments(
    type: string,
    returnSchema: string,
    documents: IDocument[],
  ): Promise<DeepPartial<INameEntity>[] | undefined> {
    for (const d of documents) d.type = type;
    const result = await guardedMutation(updateDocumentsMutation(returnSchema), { documents });
    return result?.data?.updateDocuments;
  }

  static async deleteDocuments(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteDocumentsMutation, { ids: ids });
    return result?.data?.deleteDocuments;
  }

  static async insertUserDefinedTypes(returnSchema: string, udts: IUdtInsertInput[]): Promise<DeepPartial<IUdt>[] | undefined> {
    const result = await guardedMutation(insertUserDefinedTypesMutation(returnSchema), { udts });
    return result?.data?.insertUserDefinedTypes;
  }

  static async updateUserDefinedTypes(returnSchema: string, udts: IUdtUpdateInput[]): Promise<DeepPartial<IUdt>[] | undefined> {
    const result = await guardedMutation(updateUserDefinedTypesMutation(returnSchema), { udts });
    return result?.data?.updateUserDefinedTypes;
  }

  static async deleteUserDefinedTypes(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteUserDefinedTypesMutation, { ids: ids });
    return result?.data?.deleteUserDefinedTypes;
  }

  static async insertReasons(returnSchema: string, reasons: ReasonInsertInput[]): Promise<DeepPartial<IReason>[] | undefined> {
    const result = await guardedMutation(insertReasonsMutation(returnSchema), { reasons: reasons });
    return result?.data?.insertReasons;
  }

  static async updateReasons(returnSchema: string, reasons: ReasonUpdateInput[]): Promise<DeepPartial<IReason>[] | undefined> {
    const result = await guardedMutation(updateReasonsMutation(returnSchema), { reasons: reasons });
    return result?.data?.updateReasons;
  }

  static async upsertReasons(returnSchema: string, reasons: ReasonUpsertInput[]): Promise<DeepPartial<IReason>[] | undefined> {
    const result = await guardedMutation(upsertReasonsMutation(returnSchema), { reasons: reasons });
    return result?.data?.upsertReasons;
  }

  static async deleteReasons(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteReasonsMutation, { ids: ids });
    return result?.data?.deleteReasons;
  }

  static async insertTimeOffTypes(
    returnSchema: string,
    timeOffTypes: TimeOffTypeInsertInput[],
  ): Promise<DeepPartial<ITimeOffType>[] | undefined> {
    const result = await guardedMutation(insertTimeOffTypesMutation(returnSchema), { timeOffTypes: timeOffTypes });
    return result?.data?.insertTimeOffTypes;
  }

  static async updateTimeOffTypes(
    returnSchema: string,
    timeOffTypes: TimeOffTypeUpdateInput[],
  ): Promise<DeepPartial<ITimeOffType>[] | undefined> {
    const result = await guardedMutation(updateTimeOffTypesMutation(returnSchema), { timeOffTypes: timeOffTypes });
    return result?.data?.updateTimeOffTypes;
  }

  static async upsertTimeOffTypes(
    returnSchema: string,
    timeOffTypes: TimeOffTypeUpsertInput[],
  ): Promise<DeepPartial<ITimeOffType>[] | undefined> {
    const result = await guardedMutation(upsertTimeOffTypesMutation(returnSchema), { timeOffTypes: timeOffTypes });
    return result?.data?.upsertTimeOffTypes;
  }

  static async deleteTimeOffTypes(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteTimeOffTypesMutation, { ids: ids });
    return result?.data?.deleteTimeOffTypes;
  }

  static async insertTemplates(
    returnSchema: string,
    templates: TemplateInsertInput[],
  ): Promise<DeepPartial<ITemplate>[] | undefined> {
    const result = await guardedMutation(insertTemplatesMutation(returnSchema), { templates: templates });
    return result?.data?.insertTemplates;
  }

  static async updateTemplates(
    returnSchema: string,
    templates: TemplateUpdateInput[],
  ): Promise<DeepPartial<ITemplate>[] | undefined> {
    const result = await guardedMutation(updateTemplatesMutation(returnSchema), { templates: templates });
    return result?.data?.updateTemplates;
  }

  static async upsertTemplates(
    returnSchema: string,
    templates: TemplateUpsertInput[],
  ): Promise<DeepPartial<ITemplate>[] | undefined> {
    const result = await guardedMutation(upsertTemplatesMutation(returnSchema), { templates: templates });
    return result?.data?.upsertTemplates;
  }

  static async deleteTemplates(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteTemplatesMutation, { ids: ids });
    return result?.data?.deleteTemplates;
  }

  static async insertUdfs(returnSchema: string, udfInput: UdfInsertInput[]): Promise<DeepPartial<IUdf>[] | undefined> {
    const result = await guardedMutation(insertUdfsMutation(returnSchema), { udfInput: udfInput });
    return result?.data?.insertUdfs;
  }

  static async updateUdfs(returnSchema: string, udfInput: UdfUpdateInput[]): Promise<DeepPartial<IUdf>[] | undefined> {
    const result = await guardedMutation(updateUdfsMutation(returnSchema), { udfInput: udfInput });
    return result?.data?.updateUdfs;
  }

  static async deleteUdfs(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteUdfsMutation, { ids: ids });
    return result?.data?.deleteUdfs;
  }

  static async insertViews(returnSchema: string, views: ViewInsertInput[]): Promise<DeepPartial<IView>[] | undefined> {
    const result = await guardedMutation(insertViewsMutation(returnSchema), { views: views });
    return result?.data?.insertViews;
  }

  static async updateViews(returnSchema: string, views: ViewUpdateInput[]): Promise<DeepPartial<IView>[] | undefined> {
    const result = await guardedMutation(updateViewsMutation(returnSchema), { views: views });
    return result?.data?.updateViews;
  }

  static async upsertViews(returnSchema: string, views: ViewUpsertInput[]): Promise<DeepPartial<IView>[] | undefined> {
    const result = await guardedMutation(upsertViewsMutation(returnSchema), { views: views });
    return result?.data?.upsertViews;
  }

  static async deleteViews(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteViewsMutation, { ids: ids });
    return result?.data?.deleteViews;
  }

  static async insertBatch(returnSchema: string, batch: BatchInsertInput): Promise<DeepPartial<IBatch>[] | undefined> {
    const result = await guardedMutation(insertBatchMutation, { batch });
    return result?.data?.insertBatch;
  }

  static async insertAdjustmentBatch(returnSchema: string, batch: BatchInsertInput): Promise<DeepPartial<IBatch>[] | undefined> {
    const result = await guardedMutation(insertAdjustmentBatchMutation, { batch });
    return result?.data?.insertAdjustmentBatch;
  }

  static async undoReversals(employeeInfo: IEmployeeInfo[]): Promise<boolean> {
    const result = await guardedMutation(undoReversalsMutation, { employeeInfo });
    return result?.data?.undoReversals;
  }

  static async updateBatches(returnSchema: string, batches: BatchUpdateInput[]): Promise<DeepPartial<IBatch>[] | undefined> {
    // const result = await guardedMutation(updateBatchesMutation, {batches: batches});
    // return result?.data?.updateBatches;
    return undefined;
  }

  // static async upsertBatches(returnSchema: string, batches: BatchUpsertInput[]): Promise<DeepPartial<IBatch>[] | undefined> {
  //   const result = await guardedMutation(upsertBatchesMutation, {batches: batches});
  //   return result?.data?.upsertBatches;
  // }

  static async exportBatch(id: string): Promise<boolean | undefined> {
    const result = await guardedMutation(exportBatchMutation, { id: id });
    return result?.data?.exportBatch;
  }

  // static async undoBatch(id: string): Promise<boolean | undefined> {
  //   const result = await guardedMutation(undoBatchMutation, {id: id});
  //   return result?.data?.undoBatch;
  // }

  // static async closeBatch(id: string): Promise<boolean | undefined> {
  //   const result = await guardedMutation(closeBatchMutation, {id: id});
  //   return result?.data?.closeBatch;
  // }

  static async deleteBatches(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteBatchesMutation, { ids });
    return result?.data?.deleteBatches;
  }

  static async regenerateExportFile(batchId: string): Promise<string | undefined> {
    const result = await guardedMutation(regenerateExportFile, { batchId });
    return result?.data;
  }

  static async uploadFile(file: File): Promise<{ id: string }> {
    const result = await guardedMutation(uploadFileMutation, { file: file });
    return result?.data?.uploadFile;
  }

  static async uploadFiles(files: File[]): Promise<{ id: string }[]> {
    const result = await guardedMutation(uploadFilesMutation, { files: files });
    return result?.data?.uploadFiles;
  }

  static async saveEmployeeTimecards(timecards: SaveTimecardInput[]): Promise<boolean | undefined> {
    for (const et of timecards ?? []) {
      for (const t of et.timecards ?? []) {
        if (typeof t.fields === 'string') {
          console.trace('Fields must be an object, not a string');
          throw new Error('Fields must be an object, not a string');
        }
      }
    }

    const result = await guardedMutation(saveEmployeeTimecardsMutation, { timecards });
    return result?.data?.saveEmployeeTimecards;
  }

  static async saveTestEmployeeTimecards(timecards: SaveTimecardInput[]): Promise<boolean | undefined> {
    const result = await guardedMutation(saveTestEmployeeTimecardsMutation, { timecards });
    return result?.data?.saveTestEmployeeTimecards;
  }

  static async saveRuleTable(table: IRuleSetTable): Promise<boolean | undefined> {
    const result = await guardedMutation(saveRuleTableMutation, { table });
    return result?.data?.saveRuleTable;
  }

  static async saveJobTimecards(timecards: TimecardInput[]): Promise<boolean | undefined> {
    const deleteTimecards: JobInfoInput[] = [];
    const timecardsToSave: TimecardInput[] = [];
    for (const t of timecards ?? []) {
      if (typeof t.fields === 'string') {
        console.trace('Fields must be an object, not a string');
        throw new Error('Fields must be an object, not a string');
      }

      if (!t.jobId || !t.earnedDate) {
        continue;
      }

      if (!t.reasonId) {
        if (deleteTimecards.some((tc) => tc.jobId === t.jobId && tc.dates.some((d) => d.utc === t.earnedDate.utc))) {
          continue;
        }
        let jobInfo = deleteTimecards.find((tc) => tc.jobId === t.jobId);
        if (jobInfo) {
          jobInfo.dates.push(t.earnedDate);
        } else {
          jobInfo = { jobId: t.jobId, dates: [t.earnedDate] };
        }
        deleteTimecards.push(jobInfo);
      } else {
        timecardsToSave.push(t);
      }
    }

    if (deleteTimecards.length > 0) {
      await guardedMutation(deleteJobTimecardsMutation, { jobInfo: deleteTimecards });
    }

    if (timecardsToSave.length > 0) {
      const result = await guardedMutation(saveJobTimecardsMutation, { timecards: timecardsToSave });
      return result?.data?.saveJobTimecards;
    }

    return true;
  }

  static async insertResponses(responses: IInsertResponseInput[]): Promise<boolean | undefined> {
    const result = await guardedMutation(insertResponsesMutation(), { responses });
    return result?.data?.insertResponses;
  }

  static async deleteJobTimecards(jobInfo: JobInfoInput[]): Promise<boolean | undefined> {
    const result = await guardedMutation(deleteJobTimecardsMutation, { jobInfo });
    return result?.data?.deleteJobTimecards;
  }

  static async runTests(filter?: TimecardTestFilterInput): Promise<boolean> {
    const result = await guardedMutation(runTestsMutation, { filter });
    return result?.data?.runTests;
  }

  static async insertTimecardTests(
    returnSchema: string,
    timecardTests: TimecardTestInsertInput[],
  ): Promise<DeepPartial<ITimecardTest>[] | undefined> {
    const result = await guardedMutation(insertTimecardTestsMutation(returnSchema), { timecardTests });
    return result?.data?.insertTimecardTests;
  }

  static async updateTimecardTests(
    returnSchema: string,
    timecardTests: TimecardTestUpdateInput[],
  ): Promise<DeepPartial<ITimecardTest>[] | undefined> {
    const result = await guardedMutation(updateTimecardTestsMutation(returnSchema), { timecardTests });
    return result?.data?.updateTimecardTests;
  }

  static async deleteTimecardTests(ids: string[]): Promise<boolean | undefined> {
    const result = await guardedMutation(deleteTimecardTestsMutation, { ids });
    return result?.data?.deleteTimecardTests;
  }

  static async runTimecardRules(employeeId: string, effective: DateRange): Promise<boolean | undefined> {
    const result = await guardedMutation(runTimecardRulesMutation, { employeeId, effective });
    return result?.data?.runTimecardRules;
  }

  static async runTestTimecardRules(employeeId: string, effective: DateRange): Promise<boolean | undefined> {
    const result = await guardedMutation(runTestTimecardRulesMutation, { employeeId, effective });
    return result?.data?.runTestTimecardRules;
  }

  static async insertTimeOffAccruals(
    returnSchema: string,
    timeOffAccruals: TimeOffAccrualInsertInput[],
  ): Promise<ITimeOffType[] | undefined> {
    const result = await guardedMutation(insertTimeOffAccrualMutation, { timeOffAccruals });
    return result?.data?.insertTimeOffAccruals;
  }

  static async updateTimeOffAccruals(
    returnSchema: string,
    timeOffAccruals: TimeOffAccrualUpdateInput[],
  ): Promise<ITimeOffType[] | undefined> {
    const result = await guardedMutation(updateTimeOffAccrualMutation, { timeOffAccruals });
    return result?.data?.updateTimeOffAccruals;
  }

  static async deleteTimeOffAccruals(ids: string[]): Promise<string | undefined> {
    const result = await guardedMutation(deleteTimeOffAccrualsMutation, { ids: ids });
    return result?.data?.deleteTimeOffAccruals;
  }

  static async saveValidationRules(rules: ValidationRuleInput[]): Promise<boolean | undefined> {
    const result = await guardedMutation(saveValidationRulesMutation, { rules: rules });
    return result?.data?.saveValidationRules;
  }

  static async insertTimeOffRequests(
    returnSchema: string,
    timeOffRequest: TimeOffRequestInsertInput[],
  ): Promise<DeepPartial<ITimeOffRequest>[] | undefined> {
    const result = await guardedMutation(insertTimeOffRequestsMutation(returnSchema), { timeOffRequest });
    return result?.data?.insertTimeOffRequests;
  }

  static async updateTimeOffRequests(
    returnSchema: string,
    timeOffRequest: TimeOffRequestUpdateInput[],
  ): Promise<DeepPartial<ITimeOffRequest>[] | undefined> {
    const result = await guardedMutation(updateTimeOffRequestsMutation(returnSchema), { timeOffRequest });
    return result?.data?.updateTimeOffRequests;
  }

  static async deleteTimeOffRequests(ids: string[]): Promise<boolean | undefined> {
    const result = await guardedMutation(deleteTimeOffRequestsMutation, { ids });
    return result?.data?.deleteTimeOffRequests;
  }

  static async transitionTimeOffRequest(timeOffRequestId: string, event: string): Promise<ITimeOffRequestStatus> {
    const result = await guardedMutation(transitionTimeOffRequestMutation, { timeOffRequestId, event });
    return result?.data?.transitionTimeOffRequest;
  }

  static async postSchedule(postedDate: UTC): Promise<boolean | undefined> {
    const result = await guardedMutation(postScheduleMutation, { postedDate: postedDate });
    return result?.data?.postSchedule;
  }

  static async runAutoSchedule(jobsToFill: { jobId: string; earnedDate: UTC }[]): Promise<boolean | undefined> {
    const result = await guardedMutation(runAutoScheduleMutation, { jobsToFill });
    return result?.data?.runAutoSchedule;
  }
}

const _documentTypeApiMap: Record<string, string> = {
  timeofftypes: 'timeOffTypes',
};

export async function archiveUnArchive(
  fn: 'archive' | 'unArchive',
  type?: UdfDocumentType,
  item?: { id: string; name: string },
  endDate?: UTC,
): Promise<boolean> {
  if (!type || !item) return false;
  const name = `${fn}${pascalCase(_documentTypeApiMap[type] ?? type)}`;
  const archiveData: { ids: string[]; endDate?: UTC } = { ids: [item.id] };
  let archiveMutation: DocumentNode;
  if ((type === 'employees' || type === 'areas' || type === 'groups') && fn === 'archive') {
    archiveMutation = gql`
      mutation($ids: [ID!]!, $endDate: Date) {
        ${name}(ids: $ids, endDate: $endDate)
      }
    `;
    archiveData.endDate = endDate;
  } else {
    archiveMutation = gql`
      mutation($ids: [ID!]!) {
        ${name}(ids: $ids)
      }
    `;
  }

  const result = await guardedMutation(archiveMutation, archiveData);
  if (result) {
    notifications.success(`Successfully ${capitalCase(fn)} ${item.name ?? 'item'}`);
    return true;
  }
  notifications.error(`${capitalCase(fn)} ${item.name ?? 'item'} failed`);
  return false;
}
